/**
 * Task agent — the pure brain behind the Tasks board's agentic affordances.
 *
 * Two jobs, both deterministic and store-free:
 *
 *  1. `suggestForTask` — the per-card "Argo suggests" hint, compiled from
 *     the task's own live signals (blocked / overdue / due-soon / stale /
 *     checklist progress / linked-ticket state). Pure and instant — every
 *     card can afford it without a network call.
 *
 *  2. `requestAssist` — the "Ask Argo to help / draft / complete" seam.
 *     Mirrors the hygieneDrafter contract: the real Claude worker is
 *     INJECTED via `ctx.runWorker` (the UI wires it to argoWorker when
 *     `VITE_AGENT_API_URL` is configured); every failure mode — absent
 *     worker, sync throw, rejection, timeout, malformed payload — degrades
 *     to the deterministic `fallbackAssist`. `requestAssist` ALWAYS
 *     resolves, and the `source` field ('worker' | 'fallback') is the
 *     honest label the UI must surface.
 *
 * The result is a GOVERNED PROPOSAL: nothing here writes to the store.
 * The UI shows the proposal and the operator applies each part as a
 * normal, timeline-logged task edit (add subtasks, save draft to notes,
 * mark done).
 *
 * PURE seam: this module never imports argoWorker, the store, or firebase.
 */

import type { ProjectTask, TaskChecklistItem, TicketStatus } from '../db/types';

// ===========================================================================
// Suggestions — the per-task "Argo suggests" hint
// ===========================================================================

/** What the operator is asking Argo to do. */
export type AssistMode = 'help' | 'draft' | 'complete';

export interface TaskSuggestion {
  /** Which signal fired (stable id — useful for tests + icons). */
  kind:
    | 'blocked'
    | 'ticket-done'
    | 'ready-to-close'
    | 'overdue'
    | 'due-soon'
    | 'next-subtask'
    | 'unassigned'
    | 'stale';
  /** One-line operator hint, e.g. "Overdue by 3d — reschedule or finish." */
  text: string;
  /** The assist mode the one-click affordance should launch with. */
  mode: AssistMode;
}

/** Live signals the suggestion engine may consult. Everything optional. */
export interface SuggestCtx {
  /** Clock override for tests. Default: Date.now(). */
  now?: number;
  /** All tasks on the board — used to resolve `blockedBy` ids to titles. */
  allTasks?: ProjectTask[];
  /** The linked engineering ticket, when the caller has it loaded. */
  ticket?: { id: string; status: TicketStatus; title?: string } | null;
}

const DAY_MS = 86_400_000;
/** A task untouched this long (and not done) is "stale". */
export const STALE_AFTER_DAYS = 14;

function dueEndMs(task: ProjectTask): number | null {
  if (!task.dueDate) return null;
  const t = new Date(`${task.dueDate}T23:59:59`).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Compile the single most useful next-step hint for a task from its live
 * signals. Returns null when nothing is notable (done tasks, quiet tasks)
 * so the board never gets noisy. Precedence: blocked → linked ticket done
 * → all subtasks done → overdue → due soon → next subtask → unassigned →
 * stale.
 */
export function suggestForTask(
  task: ProjectTask,
  ctx: SuggestCtx = {},
): TaskSuggestion | null {
  if (task.status === 'done') return null;
  const now = ctx.now ?? Date.now();

  // Blocked — either by status or by an open dependency edge.
  const openBlockers = (task.blockedBy ?? [])
    .map((id) => (ctx.allTasks ?? []).find((t) => t.id === id))
    .filter((t): t is ProjectTask => Boolean(t && t.status !== 'done'));
  if (task.status === 'blocked' || openBlockers.length > 0) {
    const first = openBlockers[0];
    return {
      kind: 'blocked',
      text: first
        ? `Waiting on "${first.title}" — unblock that first.`
        : 'Blocked — capture the blocker and plan around it.',
      mode: 'help',
    };
  }

  // Linked engineering ticket already landed → verify and close.
  if (ctx.ticket && task.ticketId === ctx.ticket.id && ctx.ticket.status === 'done') {
    return {
      kind: 'ticket-done',
      text: `Linked ticket ${ctx.ticket.id} is done — verify and close this task.`,
      mode: 'complete',
    };
  }

  // Every subtask checked but the task is still open.
  const total = task.checklist.length;
  const doneCount = task.checklist.filter((c) => c.done).length;
  if (total > 0 && doneCount === total) {
    return {
      kind: 'ready-to-close',
      text: `All ${total} subtasks complete — ready to mark done.`,
      mode: 'complete',
    };
  }

  const due = dueEndMs(task);
  if (due !== null) {
    const diff = due - now;
    if (diff < 0) {
      const daysOver = Math.max(1, Math.ceil(-diff / DAY_MS));
      return {
        kind: 'overdue',
        text: `Overdue by ${daysOver}d — finish it or reschedule.`,
        mode: 'help',
      };
    }
    const daysLeft = Math.floor(diff / DAY_MS);
    if (daysLeft <= 2) {
      const when = daysLeft === 0 ? 'today' : daysLeft === 1 ? 'tomorrow' : 'in 2 days';
      return {
        kind: 'due-soon',
        text: `Due ${when} — Argo can draft the work now.`,
        mode: 'draft',
      };
    }
  }

  // Point at the next unchecked subtask.
  const nextItem = task.checklist.find((c) => !c.done);
  if (nextItem) {
    return {
      kind: 'next-subtask',
      text: `Next subtask: "${nextItem.text}".`,
      mode: 'help',
    };
  }

  if (!task.assignee) {
    return {
      kind: 'unassigned',
      text: 'Unassigned — pick an owner so it moves.',
      mode: 'help',
    };
  }

  const updated = new Date(task.updatedAt).getTime();
  if (Number.isFinite(updated) && now - updated > STALE_AFTER_DAYS * DAY_MS) {
    const days = Math.floor((now - updated) / DAY_MS);
    return {
      kind: 'stale',
      text: `No activity for ${days}d — revive or close it.`,
      mode: 'help',
    };
  }

  return null;
}

// ===========================================================================
// Assist — worker-reasoned proposal with a MANDATORY deterministic fallback
// ===========================================================================

/** One governed proposal: never applied until the operator says so. */
export interface TaskAssist {
  /** One-paragraph read of the situation / what Argo proposes. */
  summary: string;
  /** Concrete next steps — applied (on confirm) as checklist subtasks. */
  steps: string[];
  /** For 'draft' mode: the working draft — saved (on confirm) to notes. */
  draft?: string;
  /** Honest origin — 'worker' ONLY when the real worker answered validly. */
  source: 'worker' | 'fallback';
}

/** Injected worker seam + tunables. Everything optional by design. */
export interface AssistCtx {
  /**
   * Route the prompt to the real worker; resolve with the parsed JSON
   * payload. Absent → pure deterministic fallback.
   */
  runWorker?: (prompt: string) => Promise<unknown>;
  /** Max ms to wait for the worker before falling back. Default 15 000. */
  timeoutMs?: number;
  /** Clock override for tests. */
  now?: number;
}

export const ASSIST_MODE_LABEL: Record<AssistMode, string> = {
  help: 'Plan the next step',
  draft: 'Draft the work',
  complete: 'Close it out',
};

const MAX_STEPS = 8;

/** The assist prompt sent to the worker — JSON-safe and self-contained. */
export function assistPrompt(task: ProjectTask, mode: AssistMode): string {
  const goal =
    mode === 'help'
      ? 'Propose the single best next step and a short plan to finish this task.'
      : mode === 'draft'
        ? 'Write a concrete working draft of the deliverable this task asks for.'
        : 'Verify readiness and propose how to close this task out cleanly.';
  return [
    'You are the ARGO task assistant. This is a GOVERNED PROPOSAL — the operator',
    'applies each part manually; never assume writes happen.',
    goal,
    'Reply with JSON only: {"summary":string,"steps":string[]' +
      (mode === 'draft' ? ',"draft":string' : '') +
      '}.',
    `Task: ${JSON.stringify({
      id: task.id,
      title: task.title,
      notes: task.notes ?? null,
      status: task.status,
      priority: task.priority,
      assignee: task.assignee,
      dueDate: task.dueDate,
      checklist: task.checklist.map((c) => ({ text: c.text, done: c.done })),
      account: task.accountName ?? null,
      workflow: task.workflowName ?? null,
      ticketId: task.ticketId ?? null,
    })}`,
  ].join('\n');
}

/**
 * The deterministic proposal, compiled from the task's own signals. Used
 * whenever the worker is absent or did not answer — and honestly labelled
 * `source: 'fallback'` so the operator knows no model reasoned about it.
 */
export function fallbackAssist(
  task: ProjectTask,
  mode: AssistMode,
  ctx: Pick<AssistCtx, 'now'> = {},
): TaskAssist {
  const suggestion = suggestForTask(task, { now: ctx.now });
  const remaining = task.checklist.filter((c) => !c.done).map((c) => c.text);

  if (mode === 'complete') {
    const ready = remaining.length === 0;
    return {
      summary: ready
        ? `Nothing left on the checklist for "${task.title}" — confirm the outcome and mark it done.`
        : `${remaining.length} subtask${remaining.length === 1 ? '' : 's'} still open on "${task.title}" — finish or drop them before closing.`,
      steps: ready
        ? ['Confirm the outcome with the owner', 'Mark the task done']
        : remaining.slice(0, MAX_STEPS),
      source: 'fallback',
    };
  }

  if (mode === 'draft') {
    const sections =
      remaining.length > 0
        ? remaining.map((r) => `## ${r}\n- …`).join('\n\n')
        : '## Approach\n- …\n\n## Deliverable\n- …';
    return {
      summary: `Deterministic draft skeleton for "${task.title}" — structure only; fill in the substance.`,
      steps: ['Review the skeleton', 'Fill in each section', 'Save the draft to notes'],
      draft: `# ${task.title}\n\n${task.notes ? `${task.notes}\n\n` : ''}${sections}`,
      source: 'fallback',
    };
  }

  // help
  const steps: string[] = [];
  if (suggestion) steps.push(suggestion.text);
  steps.push(...remaining.slice(0, MAX_STEPS));
  if (!task.assignee) steps.push('Assign an owner');
  if (!task.dueDate) steps.push('Set a due date');
  if (steps.length === 0) steps.push('Break the task into 2–3 concrete subtasks');
  return {
    summary: suggestion
      ? `Signals on "${task.title}": ${suggestion.text}`
      : `No pressing signals on "${task.title}" — keep it moving with the steps below.`,
    steps: steps.slice(0, MAX_STEPS),
    source: 'fallback',
  };
}

// --- Validation — never trust a worker payload shape -----------------------

function isValidAssist(v: unknown): v is { summary: string; steps: string[]; draft?: string } {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as { summary?: unknown; steps?: unknown; draft?: unknown };
  if (typeof o.summary !== 'string' || o.summary.trim() === '') return false;
  if (!Array.isArray(o.steps)) return false;
  if (!o.steps.every((s) => typeof s === 'string')) return false;
  if (o.draft !== undefined && typeof o.draft !== 'string') return false;
  return true;
}

/** Race a promise against a timeout; the timer is always cleared. */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`Task assist timed out after ${ms}ms`)), ms);
  });
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer));
}

const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Ask Argo for a governed proposal on a task. Uses `ctx.runWorker` when it
 * answers with a valid payload inside the timeout; otherwise falls back to
 * the deterministic proposal. ALWAYS resolves — the Tasks board can never
 * be blocked by a flaky or absent worker.
 */
export async function requestAssist(
  task: ProjectTask,
  mode: AssistMode,
  ctx: AssistCtx = {},
): Promise<TaskAssist> {
  const { runWorker } = ctx;
  if (runWorker) {
    try {
      const result = await withTimeout(
        // Wrapped in the try so a SYNCHRONOUS throw from runWorker also
        // routes to the fallback rather than rejecting requestAssist.
        Promise.resolve(runWorker(assistPrompt(task, mode))),
        ctx.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      );
      if (isValidAssist(result)) {
        const steps = result.steps.map((s) => s.trim()).filter(Boolean).slice(0, MAX_STEPS);
        return {
          summary: result.summary.trim(),
          steps,
          ...(result.draft && result.draft.trim() !== '' ? { draft: result.draft } : {}),
          source: 'worker',
        };
      }
    } catch {
      // Any failure mode (reject/throw/timeout) lands in the fallback below.
    }
  }
  return fallbackAssist(task, mode, ctx);
}

// ===========================================================================
// Apply helpers (still pure) — dedupe proposal steps against the checklist
// ===========================================================================

/**
 * The proposal steps NOT already on the task's checklist (case- and
 * whitespace-insensitive), in proposal order — exactly what "Add as
 * subtasks" should write, and nothing the task already tracks.
 */
export function newChecklistSteps(
  steps: string[],
  checklist: TaskChecklistItem[],
): string[] {
  const have = new Set(checklist.map((c) => c.text.trim().toLowerCase()));
  const out: string[] = [];
  for (const raw of steps) {
    const s = raw.trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (have.has(key)) continue;
    have.add(key);
    out.push(s);
  }
  return out;
}
